import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Layout, Menu, Table, Button, Modal, Form, Input, InputNumber, 
  message, Space, Tag, Popconfirm 
} from 'antd';
import { 
  DashboardOutlined, 
  KeyOutlined, 
  FileTextOutlined, 
  LogoutOutlined,
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { adminAPI } from '../../utils/api';
import './AdminDashboard.css';

const { Header, Sider, Content } = Layout;

const AuthCodeManagement = () => {
  const navigate = useNavigate();
  const [authCodes, setAuthCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [resultModalVisible, setResultModalVisible] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    loadAuthCodes();
  }, []);

  const loadAuthCodes = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAuthCodes();
      if (response && response.success) {
        setAuthCodes(response.authCodes);
      }
    } catch (error) {
      message.error('加载授权码失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values) => {
    try {
      const response = await adminAPI.createAuthCode(values);
      if (response && response.success) {
        message.success(`成功生成 ${values.count || 1} 个授权码`);
        setGeneratedCodes(response.codes || []);
        setModalVisible(false);
        setResultModalVisible(true);
        form.resetFields();
        loadAuthCodes();
      }
    } catch (error) {
      message.error('生成授权码失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await adminAPI.deleteAuthCode(id);
      if (response && response.success) {
        message.success('删除成功');
        loadAuthCodes();
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      message.success('授权码已复制');
    });
  };

  const handleCopyAll = () => {
    const codesText = generatedCodes.join('\n');
    navigator.clipboard.writeText(codesText).then(() => {
      message.success(`已复制 ${generatedCodes.length} 个授权码`);
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    message.success('已退出登录');
    navigate('/XiaoXin/login');
  };

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: '仪表板',
      onClick: () => navigate('/XiaoXin/dashboard')
    },
    {
      key: 'authcodes',
      icon: <KeyOutlined />,
      label: '授权码管理',
      onClick: () => navigate('/XiaoXin/authcodes')
    },
    {
      key: 'results',
      icon: <FileTextOutlined />,
      label: '使用记录',
      onClick: () => navigate('/XiaoXin/results')
    }
  ];

  const columns = [
    {
      title: '授权码',
      dataIndex: 'code',
      key: 'code',
      render: (code) => <code style={{ fontSize: 13 }}>{code}</code>
    },
    {
      title: '备注',
      dataIndex: 'note',
      key: 'note',
      render: (note) => note || '-'
    },
    {
      title: '状态',
      dataIndex: 'used',
      key: 'used',
      render: (used) => (
        <Tag color={used ? 'success' : 'default'}>
          {used ? '已使用' : '未使用'}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time) => new Date(time).toLocaleString('zh-CN')
    },
    {
      title: '使用时间',
      dataIndex: 'used_at',
      key: 'used_at',
      render: (time) => time ? new Date(time).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(record.code)}
          >
            复制授权码
          </Button>
          <Popconfirm
            title="确认删除此授权码？"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Layout className="admin-layout">
      <Sider className="admin-sider" width={240}>
        <div className="admin-sider-header">
          <div className="admin-logo">💕 RPI</div>
          <div className="admin-subtitle">管理后台</div>
        </div>
        <Menu
          mode="inline"
          selectedKeys={['authcodes']}
          items={menuItems}
          className="admin-menu"
        />
        <div className="admin-sider-footer">
          <Button 
            type="text" 
            icon={<LogoutOutlined />} 
            onClick={handleLogout}
            block
          >
            退出登录
          </Button>
        </div>
      </Sider>

      <Layout>
        <Header className="admin-header">
          <h1>授权码管理</h1>
        </Header>

        <Content className="admin-content">
          <div style={{ marginBottom: 16 }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => setModalVisible(true)}
            >
              生成授权码
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={authCodes}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 20 }}
          />

          <Modal
            title="生成授权码"
            open={modalVisible}
            onOk={() => form.submit()}
            onCancel={() => {
              setModalVisible(false);
              form.resetFields();
            }}
            okText="生成"
            cancelText="取消"
          >
            <Form
              form={form}
              onFinish={handleCreate}
              layout="vertical"
            >
              <Form.Item
                name="count"
                label="生成数量"
                initialValue={1}
                rules={[{ required: true, message: '请输入数量' }]}
              >
                <InputNumber min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="note"
                label="备注（可选）"
              >
                <Input.TextArea rows={3} placeholder="为这批授权码添加备注" />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title={`成功生成 ${generatedCodes.length} 个授权码`}
            open={resultModalVisible}
            onCancel={() => setResultModalVisible(false)}
            footer={[
              <Button key="close" onClick={() => setResultModalVisible(false)}>
                关闭
              </Button>,
              <Button 
                key="copy" 
                type="primary" 
                icon={<CopyOutlined />}
                onClick={handleCopyAll}
              >
                复制全部
              </Button>
            ]}
            width={600}
          >
            <div style={{ marginBottom: 16 }}>
              <Tag color="success">
                已生成 {generatedCodes.length} 个授权码
              </Tag>
              <span style={{ marginLeft: 8, color: '#666', fontSize: 12 }}>
                点击"复制全部"可一次性复制所有授权码
              </span>
            </div>
            <div 
              style={{ 
                maxHeight: 400, 
                overflowY: 'auto',
                background: '#f5f5f5',
                padding: 16,
                borderRadius: 4,
                border: '1px solid #d9d9d9'
              }}
            >
              {generatedCodes.map((code, index) => (
                <div 
                  key={index}
                  style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    marginBottom: index < generatedCodes.length - 1 ? 8 : 0,
                    background: '#fff',
                    borderRadius: 4,
                    border: '1px solid #e8e8e8'
                  }}
                >
                  <code style={{ 
                    fontSize: 14, 
                    fontWeight: 'bold',
                    color: '#1890ff',
                    fontFamily: 'Monaco, monospace'
                  }}>
                    {code}
                  </code>
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => handleCopy(code)}
                  >
                    复制
                  </Button>
                </div>
              ))}
            </div>
          </Modal>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AuthCodeManagement;
